import React, { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, PlayCircle, Sparkles } from "lucide-react";
import { useSelector } from "react-redux";
import CartButton from "./CartButton";
import CommonButton from "../CommonButton";
import CartDrawer, { CartItem } from "./ProductDetailComponent.tsx/CartDrawer";
import { useAlert } from "./AlertContext";
import { RootState } from "../../redux/rootReducer";
import {
  useCreateCartMutation,
  useLazyGetCartQuery,
  useUpdateCartMutation,
} from "../../redux/actions/Customer/cartCustomer";

interface Props {
  items: any[];
  handleViewAll?: () => void;
  showViewAll?: boolean;
  layout?: "overlay" | "below";
  title?: React.ReactNode;
  description?: string;
  badgeText?: string;
  showallDetails?: boolean;
  onAddToCartClick?: (product: any) => void;
  handleCheckoutClick?: () => void;
  handleProductPage?: (product: any) => void;
}

const VideoCarousel: React.FC<Props> = ({
  items = [],
  handleViewAll,
  showViewAll = true,
  layout = "overlay",
  title,
  description,
  badgeText = "Trending",
  showallDetails = true,
  onAddToCartClick,
  handleCheckoutClick,
  handleProductPage,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const { showConfirm } = useAlert();

  // Redux API Mutations & Queries
  const [createCart] = useCreateCartMutation();
  const [updateCart] = useUpdateCartMutation();
  const [getCartTrigger, { data: cartData }] = useLazyGetCartQuery();

  const isLoggedIn = useSelector(
    (state: RootState) => state.auth?.customer?.isLoggedIn,
  );

  // Synchronize server cart data with local CartDrawer state
  const syncCartWithServer = (serverItems: any[]) => {
    const mappedItems: CartItem[] = serverItems.map((item) => ({
      id: item.cartItemId?.toString() || "",
      productId: item.productId,
      title: item.productName || "",
      price: `AED ${item.unitSalePrice || item.unitPrice || 0}`,
      image: item.imageUrl || "",
      quantity: item.quantity || 1,
      isMandatory: item.isMandatory,
    }));
    setCart(mappedItems);
  };

  const fetchLatestCart = async () => {
    try {
      const res = await getCartTrigger().unwrap();
      const fetchedItems = res?.data?.items || [];
      syncCartWithServer(fetchedItems);
    } catch (error) {
      console.error("Failed to fetch latest cart:", error);
    }
  };

  // Live Database Cart Handlers
  const handleAdd = async (product: any) => {
    const productId =
      product.PetId || product.id || product.productId || product.productCode;

    try {
      await createCart({
        productId: Number(productId),
        quantity: 1,
      }).unwrap();
      await fetchLatestCart();
      setIsOpen(true);
      onAddToCartClick?.(product);
    } catch (error: any) {
      if (isLoggedIn) {
        showConfirm({
          title: "Product Error",
          message: error?.data?.Message || "Failed to Add to Cart",
          type: "error",
          confirmText: "Ok",
        });
      }
    }
  };

  const handleIncrement = async (itemId: string) => {
    const itemsList = cartData?.data?.items || [];
    const targetItem = itemsList.find(
      (item: any) => item.cartItemId?.toString() === itemId?.toString(),
    );

    if (!targetItem) return;

    try {
      await updateCart({
        cartItemId: Number(itemId),
        quantity: targetItem.quantity + 1,
      }).unwrap();
      await fetchLatestCart();
    } catch (error: any) {
      showConfirm({
        title: "Cart",
        message: error?.data?.Message || "Failed to update quantity",
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleDecrement = async (itemId: string) => {
    const itemsList = cartData?.data?.items || [];
    const targetItem = itemsList.find(
      (item: any) => item.cartItemId?.toString() === itemId?.toString(),
    );

    if (!targetItem) return;

    try {
      if (targetItem.quantity <= 1) {
        await handleRemoveProduct(itemId);
      } else {
        await updateCart({
          cartItemId: Number(itemId),
          quantity: targetItem.quantity - 1,
        }).unwrap();
        await fetchLatestCart();
      }
    } catch (error: any) {
      showConfirm({
        title: "Cart",
        message: error?.data?.Message || "Failed to update quantity",
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleRemoveProduct = async (itemId: string) => {
    try {
      await updateCart({
        cartItemId: Number(itemId),
        quantity: 0,
      }).unwrap();
      await fetchLatestCart();
    } catch (error: any) {
      showConfirm({
        title: "Cart",
        message: error?.data?.Message || "Failed to remove product",
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleAddToCartFromSlider = async (sliderProduct: any) => {
    try {
      await createCart({
        productId: Number(
          sliderProduct.PetId ||
            sliderProduct.productId ||
            sliderProduct.ProductId ||
            sliderProduct.id,
        ),
        quantity: 1,
      }).unwrap();
      await fetchLatestCart();
      setIsOpen(true);
    } catch (error: any) {
      showConfirm({
        title: "Cart",
        message: error?.data?.Message || "Failed to Add product",
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  // Video playback controls
  const pauseAllVideos = () => {
    videoRefs.current.forEach((video) => {
      video?.pause();
    });
  };

  const playVideo = async (video?: HTMLVideoElement | null) => {
    if (!video) return;
    try {
      await video.play();
    } catch {
      // Autoplay handled/blocked by browser
    }
  };

  // Scroll controls
  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -320 : 320,
      behavior: "smooth",
    });
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  return (
    <div className="px-4 py-5 bg-softCream rounded-2xl relative overflow-hidden">
      {/* HEADER SECTION */}
      <div className="max-w-7xl mx-auto mb-4 relative flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="text-left">
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-accent/30 text-logoHeading text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              Paws & Play
            </span>
          </div>

          {title && (
            <h2 className="text-xl md:text-3xl font-extrabold text-logoHeading leading-tight">
              {title}
            </h2>
          )}

          {description && <p className="text-gray-600 mt-2">{description}</p>}
        </div>

        {showViewAll && (
          <CommonButton
            text="View All Videos"
            icon={<ChevronRight size={16} />}
            iconPosition="right"
            variant="outline"
            iconTextClassName="py-2 px-2"
            onClick={handleViewAll}
          />
        )}
      </div>

      {/* CAROUSEL CONTAINER */}
      <div className="w-full relative">
        {/* Left Arrow */}
        <div className="absolute -left-3 md:-left-6 top-1/2 -translate-y-1/2 z-40">
          <CommonButton
            icon={<ChevronLeft size={25} />}
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            className="bg-white shadow-md rounded-full hover:bg-gray-50 transition disabled:opacity-30"
          />
        </div>

        {/* Right Arrow */}
        <div className="absolute -right-3 md:-right-6 top-1/2 -translate-y-1/2 z-40">
          <CommonButton
            icon={<ChevronRight size={25} />}
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            className="bg-white shadow-md rounded-full hover:bg-gray-50 transition disabled:opacity-30"
          />
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-6 overflow-x-auto pb-8 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden scroll-smooth"
        >
          {items.map((item, index) => {
            // Type identification & normalization
            const isPet = Boolean(item.PetId || item.PetCode || item.PetName);
            const isCustom = item.ItemType === "Custom";

            const pId =
              item.PetId ||
              item.ProductId ||
              item.productId ||
              item.id ||
              item.Id ||
              item.productCode ||
              index;

            const productName =
              item.PetName ||
              item.name ||
              item.productname ||
              item.Title ||
              item.Name ||
              "Featured Item";

            // Extract video URL (handles custom mp4 in ImageUrl if present)
            const rawVideo =
              item.VideoUrl ||
              item.videoUrl ||
              (typeof item.ImageUrl === "string" &&
              item.ImageUrl.toLowerCase().endsWith(".mp4")
                ? item.ImageUrl
                : null);

            // Extract thumbnail/product image
            const productImage =
              item.ProductImage ||
              item.ThumbnailUrl ||
              item.thumbnailUrl ||
              (typeof item.imageUrl === "string" &&
              !item.imageUrl.toLowerCase().endsWith(".mp4")
                ? item.imageUrl
                : null) ||
              (typeof item.ImageUrl === "string" &&
              !item.ImageUrl.toLowerCase().endsWith(".mp4")
                ? item.ImageUrl
                : null) ||
              item.productimage ||
              null;

            // Pricing logic
            const originalPrice = Number(
              item.Price ?? item.price ?? item.originalPrice ?? 0,
            );
            const salePrice = Number(
              item.SalePrice ?? item.salePrice ?? item.saleprice ?? 0,
            );
            const bookPrice = Number(item.BookPrice ?? 0);

            const hasOriginalPrice = originalPrice > 0;
            const hasSalePrice = salePrice > 0 && salePrice < originalPrice;
            const displayPrice = hasSalePrice
              ? salePrice
              : originalPrice > 0
                ? originalPrice
                : bookPrice > 0
                  ? bookPrice
                  : null;

            // Badge text resolution
            const resolvedBadge =
              item.Tag ||
              (isPet ? item.Breed || "Pet" : null) ||
              badgeText;

            // Cart matching
            const itemsList = cartData?.data?.items || [];
            const cartMatch = itemsList.find(
              (cItem: any) => Number(cItem.productId) === Number(pId),
            );
            const liveQty = cartMatch ? cartMatch.quantity : 0;
            const cartItemId = cartMatch ? cartMatch.cartItemId : "";

            const handleCardClick = () => {
              if (item.RedirectUrl) {
                window.location.href = item.redirectUrl;
                return;
              }
              handleProductPage?.(item);
            };

            return (
              <div
                key={pId}
                onClick={handleCardClick}
                className="group w-[240px] md:w-[280px] flex flex-col flex-shrink-0 snap-start bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onMouseEnter={() => {
                  pauseAllVideos();
                  if (rawVideo) {
                    playVideo(videoRefs.current[index]);
                  }
                }}
                onMouseLeave={pauseAllVideos}
              >
                {/* MEDIA WRAPPER */}
                <div className="relative h-[380px] md:h-[420px] overflow-hidden bg-slate-100">
                  {rawVideo ? (
                    <video
                      ref={(element) => {
                        videoRefs.current[index] = element;
                      }}
                      src={rawVideo}
                      loop
                      poster={productImage || undefined}
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  ) : productImage ? (
                    <img
                      src={productImage}
                      alt={productName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-100">
                      <PlayCircle className="text-slate-400" size={42} />
                    </div>
                  )}

                  {/* PLAY ICON OVERLAY */}
                  {rawVideo && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-transparent transition-colors">
                      <PlayCircle className="text-white/70 w-8 h-8 group-hover:opacity-0 transition-opacity" />
                    </div>
                  )}

                  {/* BADGE */}
                  {resolvedBadge && (
                    <div className="absolute top-4 left-4 z-10">
                      <div className="bg-offerColor text-logoHeading text-xs font-black px-2.5 py-1 rounded-lg flex items-center gap-1 shadow-sm">
                        <Sparkles size={10} />
                        {resolvedBadge}
                      </div>
                    </div>
                  )}

                  {/* OVERLAY DETAILS */}
                  {layout === "overlay" && (
                    <div className="absolute bottom-3 left-3 right-3 z-20">
                      {showallDetails ? (
                        <div className="flex items-center gap-2.5 backdrop-blur-md bg-white/95 p-3 rounded-2xl border border-white/60 shadow-lg">
                          {productImage ? (
                            <img
                              src={productImage}
                              alt={productName}
                              className="w-12 h-12 rounded-xl object-cover ring-2 ring-mainBrandColor/20 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-slate-200 flex-shrink-0" />
                          )}

                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">
                              {productName}
                            </p>

                            {/* PRICE ROW */}
                            {displayPrice !== null && (
                              <div className="flex items-baseline gap-1.5 flex-wrap">
                                <span className="text-sm font-extrabold text-logoHeading">
                                  AED {displayPrice.toFixed(2)}
                                </span>
                                {hasSalePrice && (
                                  <span className="text-2xs text-gray-400 line-through">
                                    AED {originalPrice.toFixed(2)}
                                  </span>
                                )}
                              </div>
                            )}

                            {isPet && bookPrice > 0 && (
                              <p className="text-2xs font-semibold text-emerald-600">
                                Booking: AED {bookPrice.toFixed(2)}
                              </p>
                            )}
                          </div>

                          {/* ACTION BUTTON */}
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className="flex-shrink-0"
                          >
                            {!isPet && !isCustom ? (
                              <CartButton
                                qty={liveQty}
                                onAdd={() => handleAdd(item)}
                                onIncrement={() =>
                                  handleIncrement(cartItemId.toString())
                                }
                                onDecrement={() =>
                                  handleDecrement(cartItemId.toString())
                                }
                              />
                            ) : (
                              <CommonButton
                                text={isPet ? "View" : "Open"}
                                variant="filled"
                                rounded="md"
                                className="bg-mainBrandColor text-white text-xs font-bold px-3 py-1.5"
                                onClick={() => handleCardClick()}
                              />
                            )}
                          </div>
                        </div>
                      ) : (
                        <CommonButton
                          text={isPet ? "View" : "Book Now"}
                          variant="filled"
                          rounded="md"
                          className="bg-mainBrandColor w-full"
                          textContainerClassName="py-2 px-2"
                          onClick={() => handleCardClick()}
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* BELOW LAYOUT */}
                {layout === "below" && (
                  <div className="p-4 flex flex-col gap-3 flex-1 justify-between">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-800 text-base leading-tight truncate">
                          {productName}
                        </h3>

                        {/* PRICE ROW */}
                        {displayPrice !== null && (
                          <div className="flex items-baseline gap-1.5 mt-1 flex-wrap">
                            <span className="text-base font-extrabold text-logoHeading">
                              AED {displayPrice.toFixed(2)}
                            </span>
                            {hasSalePrice && (
                              <span className="text-xs text-gray-400 line-through">
                                AED {originalPrice.toFixed(2)}
                              </span>
                            )}
                          </div>
                        )}

                        {isPet && bookPrice > 0 && (
                          <p className="text-xs font-semibold text-emerald-600 mt-0.5">
                            Booking: AED {bookPrice.toFixed(2)}
                          </p>
                        )}
                      </div>

                      {productImage ? (
                        <img
                          src={productImage}
                          className="w-12 h-12 rounded-lg object-cover border border-mediumgray flex-shrink-0"
                          alt={productName}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-100 border border-mediumgray flex-shrink-0" />
                      )}
                    </div>

                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="w-full mt-2"
                    >
                      {!isPet && !isCustom ? (
                        <CartButton
                          qty={liveQty}
                          onAdd={() => handleAdd(item)}
                          onIncrement={() =>
                            handleIncrement(cartItemId.toString())
                          }
                          onDecrement={() =>
                            handleDecrement(cartItemId.toString())
                          }
                        />
                      ) : (
                        <CommonButton
                          text={isPet ? "View Pet" : "Explore"}
                          variant="filled"
                          rounded="md"
                          className="bg-mainBrandColor w-full text-white py-2 text-xs font-bold"
                          onClick={() => handleCardClick()}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Cart Drawer Component */}
      <CartDrawer
        cart={cart}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onIncrement={handleIncrement}
        onDecrement={handleDecrement}
        onRemove={handleRemoveProduct}
        onAddToCart={handleAddToCartFromSlider}
        handleCheckoutClick={handleCheckoutClick!}
      />
    </div>
  );
};

export default VideoCarousel;